import React, { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { LogActionTypes, useDataLogging } from '../../data-logging';
import { useFullBook, useUser } from '../../hooks';
import { BlockType } from 'src/config/api/models/Block';
import {
  DrawerContainer,
  DrawerContent,
  DrawerFirstItem,
  DrawerItem,
  DrawerSecondItem,
  DrawerWrapper,
} from './Atoms';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import { getContentToMap } from './helpers';
import {
  setActiveItem,
  setNavigationBarStatus,
} from '../../slices/navigationBar/navigationBarSlice';
import { Tooltip } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { setPresentingIndex } from '../../slices/presentationMode/presentationModeSlice';
import { NavbarContent } from '../../slices/navigationBar/type';
import { TableOfContentsNavBarInfo } from './content/table-of-contents/TableOfContentsNavBarInfo';

const NavigationBar: React.FC = () => {
  const history = useHistory();
  const { isTeacher, isStudent } = useUser();
  const dispatch = useAppDispatch();
  const log = useDataLogging();
  const blocks = useAppSelector((state) => state.blocks.blocks);
  const { currentBookId, currentChapterId } = useAppSelector((state) => state.trajectories);
  const trajectory = useAppSelector((state) => state.trajectories.trajectory);
  const { isNavigationBarOpen, activeItem, place } = useAppSelector((state) => state.navigationBar);
  const showPresentationMode = useAppSelector(
    (state) => state.presentationMode.showPresentationMode
  );
  const blocksToPresent = useAppSelector((state) => state.presentationMode.blocksToPresent);
  const blocksToObserve = useAppSelector((state) => state.presentationMode.blocksToObserve);
  const presentingIndex = useAppSelector((state) => state.presentationMode.presentingIndex);
  const clickedOnNavBar = useAppSelector((state) => state.tableOfContents.clickedOnNavBar);
  const expandedIndex = useAppSelector((state) => state.tableOfContents.expandedIndex);

  //const blockToScroll = useAppSelector((state) => state.blocks.blockToScroll);

  const fullBook = useFullBook(currentBookId);
  const bookInfo = fullBook.data;

  const handleScrollToTop = useCallback(
    (id: string, type?: BlockType) => {
      if (type === BlockType['CLUSTER']) {
        log(LogActionTypes.NAVIGATION_BAR_ROUTE_CLUSTER);
      } else {
        log(LogActionTypes.NAVIGATION_BAR_SCROLL_TOP);
      }
      const elem = document.getElementById(id);

      if (elem) {
        elem.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    },
    [log]
  );

  const handleNavPresentationMode = useCallback(
    (id: string) => {
      const index = blocksToPresent?.findIndex(
        (item) => item.id === id.replace('trajectory_block_', '')
      );

      if (index !== -1 && typeof index === 'number') return dispatch(setPresentingIndex(index));
      return;
    },
    [blocksToPresent, dispatch]
  );

  const handleNavigation = useCallback(
    (id: string, type?: BlockType) => {
      if (showPresentationMode) {
        if (!type) {
          return dispatch(setPresentingIndex(0));
        }
        handleNavPresentationMode(id);
      }
      return handleScrollToTop(id, type);
    },
    [dispatch, handleNavPresentationMode, handleScrollToTop, showPresentationMode]
  );

  const handleNavigationBarStatus = useCallback(() => {
    if (isNavigationBarOpen) {
      log(LogActionTypes.NAVIGATION_BAR_CLOSE);
    } else {
      log(LogActionTypes.NAVIGATION_BAR_OPEN);
    }
    dispatch(setNavigationBarStatus(!isNavigationBarOpen));
  }, [dispatch, isNavigationBarOpen, log]);

  const elementsToObserveList: string[] = useMemo(() => {
    if (blocks?.length === 0) return [];
    if (!blocks) return [];

    if (showPresentationMode && blocksToPresent && blocksToObserve.length) {
      return blocksToObserve;
    }

    let newBlocksToObserve = [...blocks];
    const newBlocks = newBlocksToObserve
      .filter((item) => item.type === BlockType.CLUSTER)
      .map((block) => {
        return `trajectory_block_${block.id}`;
      });

    return Array.from(new Set(newBlocks));
  }, [blocks, blocksToPresent, showPresentationMode, blocksToObserve]);

  const activeId = useIntersectionObserver(elementsToObserveList);

  const handleChapterClick = useCallback(() => {
    log(LogActionTypes.NAVIGATION_BAR_ROUTE_TOC);
    if (isTeacher || isStudent) {
      history.push(`/nl-be/boekentas/${currentBookId}/hoofdstukken/${currentChapterId}`);
    } else {
      history.push(`/nl-be/boeken/${currentBookId}`);
    }
  }, [currentBookId, history, log, isTeacher, isStudent, currentChapterId]);

  const [contentV2, setContentV2] = useState<NavbarContent[] | null>([]);
  const content: NavbarContent[] = useMemo(() => {
    if (place === 'table-of-contents' && contentV2 && contentV2.length > 0) {
      return contentV2;
    } else if (place !== 'table-of-contents') {
      dispatch(setActiveItem(''));
      const updatedContent = getContentToMap(
        bookInfo,
        handleNavigationBarStatus,
        handleChapterClick,
        currentChapterId!,
        trajectory,
        handleNavigation,
        blocks
      );
      return updatedContent;
    } else {
      return [];
    }
  }, [
    dispatch,
    contentV2,
    place,
    bookInfo,
    handleNavigationBarStatus,
    handleChapterClick,
    currentChapterId,
    trajectory,
    handleNavigation,
    blocks,
  ]);

  const isActiveItem = useMemo(() => {
    if (!activeId || place === 'table-of-contents') return '';
    if (showPresentationMode && place === 'presentation-mode') {
      if (blocksToPresent?.[presentingIndex].id) {
        return blocksToPresent?.[presentingIndex].id;
      }
      return '';
    }
    if (place === 'trajectory') {
      return activeId.replace('trajectory_block_', '');
    }
    return '';
  }, [activeId, blocksToPresent, place, presentingIndex, showPresentationMode]);

  if (!place) return null;

  return (
    <DrawerContainer>
      {place === 'table-of-contents' && <TableOfContentsNavBarInfo setContentV2={setContentV2} />}

      <DrawerWrapper
        id="DrawerWrapper"
        open={isNavigationBarOpen}
        variant="permanent"
        className={`${isNavigationBarOpen ? 'open' : ''}`}
      >
        <DrawerContent id="DrawerContent">
          {content.map((item) => {
            const isSelectedInTableOfContents =
              clickedOnNavBar === item.id && !expandedIndex && place === 'table-of-contents';
            return (
              <DrawerItem
                tocselected={isSelectedInTableOfContents}
                activecolor={item.activecolor}
                id={item.id}
                onClick={() => item.onClick()}
                key={item.id}
                className={item.id === isActiveItem || item.id === activeItem ? 'active' : ''}
              >
                <Tooltip title={item.tooltip && !isNavigationBarOpen ? item.tooltip : ''}>
                  <span>
                    <DrawerFirstItem
                      place={place}
                      isopen={isNavigationBarOpen ? 'true' : 'false'}
                      className={item.type}
                    >
                      {item.firstItem}
                    </DrawerFirstItem>
                  </span>
                </Tooltip>
                <DrawerSecondItem
                  place={place}
                  isopen={isNavigationBarOpen ? 'true' : 'false'}
                  className={item.type}
                >
                  {item.secondItem}
                </DrawerSecondItem>
              </DrawerItem>
            );
          })}
        </DrawerContent>
      </DrawerWrapper>
    </DrawerContainer>
  );
};

export default NavigationBar;
